import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Switch,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationSelector from './LocationSelector';
import { createUser, getUserGroups, UserGroup, AUTH_ERRORS, userListService } from '../services/axiosService';
import { useAuth } from '../contexts/AuthContext';
import { useUser } from '../contexts/UserContext';
import { Application } from '../constants/Application';
import { trackEvent } from '../services/trackEvent';

interface AddUserProps {
  open: boolean;
  onClose: () => void;
  userId?: string; // Optional userId for edit mode
  onUserSaved?: () => void; // Callback to refresh parent data after successful save
}

const defaultSearchResultsPerPage = 15;

const AddUser: React.FC<AddUserProps> = ({ open, onClose, userId, onUserSaved }) => {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const { getAccount, userRole } = useUser();

  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedLocationDetails, setSelectedLocationDetails] = useState<Array<{id: string, name: string}>>([]);
  const [newUser, setNewUser] = useState({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        userGroup: '',
        notes: '',
        password: '',
        confirmPassword: '',
        isFederatedUser: false,
        defaultSearchResultsPerPage: defaultSearchResultsPerPage,
        deactivated: false
    });
  const [errors, setErrors] = useState<{ username?: boolean; email?: boolean; userGroup?: boolean, password?: boolean, confirmPassword?: boolean }>({});
    const [apiError, setApiError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [loadingUserGroups, setLoadingUserGroups] = useState<boolean>(false);
  const [currentApplication, setCurrentApplication] = useState<string>(Application.Protect);
  const [account, setAccount] = useState<{ accountId: number; applicationName: string } | null>(null);
  const errorRef = useRef<HTMLParagraphElement | null>(null);
  const successRef = useRef<HTMLParagraphElement | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingUserData, setIsLoadingUserData] = useState(false);
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const isEditMode = !!userId;

  useEffect(() => {
    if (open) {
      setSelectedLocations([]);
      setNewUser({
        username: '',
        email: '',
        firstName: '',
        lastName: '',
        userGroup: '',
        notes: '',
        password: '',
        confirmPassword: '',
        isFederatedUser: false,
        defaultSearchResultsPerPage: defaultSearchResultsPerPage,
        deactivated: false
    });
    setErrors({});
    setApiError(null);
    setSuccessMessage(null);
    setShowPasswordFields(false);

    // Get and store account object
    const application = getAccount(currentApplication);
        setAccount(application);

        fetchUserGroups(application); // Pass account to fetchUserGroups
        
        // If in edit mode, fetch user data
        if (isEditMode && userId) {
          fetchUserData(userId);
        }
    }
  }, [open, isEditMode, userId]);

  useEffect(() => {
    if (apiError && errorRef.current) {
      errorRef.current.focus();
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [apiError]);

  useEffect(() => {
    if (successMessage && successRef.current) {
      successRef.current.focus();
      successRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [successMessage]);

  const fetchUserGroups = async (acc?: { accountId: number; applicationName: string }) => {
    setLoadingUserGroups(true);
    setApiError(null);
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) throw new Error(AUTH_ERRORS.NO_ACCESS_TOKEN);
      const accountObj = acc || account;
      if (!accountObj) throw new Error('Account information not available');
      const groups = await getUserGroups(accountObj.accountId, accountObj.applicationName, accessToken);
      setUserGroups(groups);
    } catch (error: any) {
      let message = t('addUser.messages.loadingUserGroups');
      if (error?.response?.data?.message) message = error.response.data.message;
      else if (error?.message) message = error.message;
      setApiError(message);
    } finally {
      setLoadingUserGroups(false);
    }
    };

  // Fetch user data for editing
  const fetchUserData = async (userIdToFetch: string) => {
    setIsLoadingUserData(true);
    setApiError(null);
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) throw new Error(AUTH_ERRORS.NO_ACCESS_TOKEN);
      
      const userData = await userListService.getUserById(accessToken, userIdToFetch);
      
      // Populate form with user data
      setNewUser({
        username: userData.login || '',
        email: userData.email || '',
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        userGroup: userData.accountRoleGroupId?.toString() || '',
        notes: userData.userNotes || '',
        password: '', // Don't populate password for security
        confirmPassword: '',
        isFederatedUser: userData.isFederatedUser,
        defaultSearchResultsPerPage: userData.defaultSearchResultsPerPage,
        deactivated: userData.deactivated || false,
      });
      
      // Set locations if available
      if (userData.locationIds && Array.isArray(userData.locationIds)) {
        setSelectedLocations(userData.locationIds.map((id: number) => id.toString()));
      }
      
      // Set external user flag
      // setIsExternal(userData.isFederatedUser || false);
      
    } catch (error: any) {
      let message = t('addUser.messages.loadingUserData');
      if (error?.response?.data?.message) message = error.response.data.message;
      else if (error?.message) message = error.message;
      setApiError(message);
    } finally {
      setIsLoadingUserData(false);
    }
  };

    const handleUserInputChange = (field: string, value: string) => {
        // Handle boolean conversion for deactivated field
        const processedValue = field === 'deactivated' ? value === 'true' : value;
        setNewUser(prev => ({ ...prev, [field]: processedValue }));
        
        // Clear errors for the current field
        if (errors[field as keyof typeof errors]) {
            setErrors(prev => ({ ...prev, [field]: false }));
        }
        
        // Clear both password field errors when typing in either password field
        if (field === 'password' || field === 'confirmPassword') {
            setErrors(prev => ({
                ...prev,
                password: false,
                confirmPassword: false
            }));
        }
    };

  const handleLocationSelect = (locations: string[], locationDetails?: Array<{id: string, name: string}>) => {
    setSelectedLocations(locations);
    setSelectedLocationDetails(locationDetails || []);
  };


    const isValidEmail = (email: string) => {

      const atIndex = email.indexOf('@');
    if (atIndex > 0) {
      const localPart = email.substring(0, atIndex);
      const domainPart = email.substring(atIndex + 1);
     
      // Check local part for invalid dot patterns
      if (localPart.includes('..') || localPart.startsWith('.') || localPart.endsWith('.')) {
        return false;
      }
     
      // Check domain part for invalid dot patterns
      if (domainPart.includes('..') || domainPart.startsWith('.') || domainPart.endsWith('.')) {
        return false;
      }
    } 
     
    return /^[^\s@]+@[a-zA-Z0-9]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?$/.test(email);
    };

  const isValidPassword = (password: string) => {
    // At least 8 characters, one lowercase, one uppercase, one number
    return (
      password.length >= 8 &&
      /[a-z]/.test(password) &&
      /[A-Z]/.test(password) &&
      /\d/.test(password)
    );
  };

  const handleAdd = async () => {
  setApiError(null);
  setSuccessMessage(null);

  const newErrors = {
    username: !newUser.username.trim(),
    email: !newUser.email.trim() || !isValidEmail(newUser.email),
    userGroup: !newUser.userGroup.trim(),
    password: !isEditMode ? (!newUser.password.trim() || !isValidPassword(newUser.password)) :
              !!(showPasswordFields && newUser.password.trim() && !isValidPassword(newUser.password)),
    confirmPassword: !isEditMode ? (!newUser.confirmPassword.trim() || newUser.password !== newUser.confirmPassword) :
                     !!(showPasswordFields && (newUser.password.trim() || newUser.confirmPassword.trim()) && newUser.password !== newUser.confirmPassword),
  };
  setErrors(newErrors);

  if (
    newErrors.username ||
    newErrors.email ||
    newErrors.userGroup ||
    newErrors.password ||
    newErrors.confirmPassword
  ) return;

  if (!account) {
    setApiError('apiResponse.addUser.errors.accountNotAvailable');
    return;
  }

  const user = {
    UserId: isEditMode? userId: null,
    FirstName: newUser.firstName,
    LastName: newUser.lastName,
    Email: newUser.email,
    Login: newUser.username,
    Password: (isEditMode && !showPasswordFields) ? null : (newUser.password == null || newUser.password == ""? null: newUser.password),
    AccountRoleGroupId: parseInt(newUser.userGroup) || null,
    LocationIds: selectedLocations.map(Number),
    UserNotes: newUser.notes,
    IsFederatedUser: newUser.isFederatedUser,
    DefaultSearchResultsPerPage: isEditMode? newUser.defaultSearchResultsPerPage: defaultSearchResultsPerPage,
    NotificationEmail: newUser.email,
    ApplicationName: account.applicationName,
    AccountId: account.accountId,
    Deactivated: newUser.deactivated,
  };

    setIsSubmitting(true);  //block and show spinner

  try {
    const accessToken = await getAccessToken();
    if (!accessToken) throw new Error(AUTH_ERRORS.NO_ACCESS_TOKEN);
      await createUser(user, accessToken);
      const locationInfo = selectedLocationDetails.map(loc => `${loc.id} - ${loc.name}`);
      trackEvent.userCreated(userRole?.isAdmin ? 'Admin' : 'Basic User', {
          isExternal: false,
          hasLocations: selectedLocations.length > 0,
          locationCount: selectedLocations.length,
          locationIds: selectedLocations,
          locationNames: selectedLocationDetails.map(loc => loc.name),
          locationInfo: locationInfo, // Format: "ID - Name"
          applicationName: account.applicationName,
          userGroup: newUser.userGroup,
          userName: newUser.username,
          userEmail: newUser.email
      });

      // Track location user management for each assigned location
      if (selectedLocationDetails.length > 0) {
          selectedLocationDetails.forEach(location => {
              trackEvent.locationUserManaged('assign', newUser.username, `${location.id} - ${location.name}`, {
                  creatorRole: userRole?.isAdmin ? 'Admin' : 'Basic User',
                  applicationName: account.applicationName,
                  isNewUser: true,
                  userEmail: newUser.email,
                  locationId: location.id,
                  locationName: location.name
              });
          });
      }
    setSuccessMessage(isEditMode ? t('addUser.messages.userUpdatedSuccess') : t('addUser.messages.userCreatedSuccess'));
    
    // Call the callback to refresh parent data
    
    
    setTimeout(() => {
      onClose();
      if (onUserSaved) {
      onUserSaved();
    }
    }, 2000);    
   
  } catch (error: any) {
    let message = isEditMode ? "apiResponse.addUser.errors.failedToUpdateUser" : "apiResponse.addUser.errors.failedToCreateUser";
    // Customize message for 500 Bad Request
    if (error?.response?.status === 500 && error?.response?.data?.message) {
      message = "apiResponse.addUser.errors.serverError";
    } else if (error?.response?.data?.message) {
      message = error.response.data.message;
    } else if (error?.message) {
      message = error.message;
    }
    setApiError(message);
    console.error('Failed to create/update user:', error);
  } finally {
    setIsSubmitting(false); // done loading
  }
};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        {isEditMode ? t('addUser.editTitle') : t('addUser.title')}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isLoadingUserData && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>{t('addUser.messages.loadingUserData')}</Typography>
          </Box>
        )}
        {!isLoadingUserData && (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mt: 2, alignItems: 'flex-start' }}>
          <Box sx={{ flex: '0 0 290px', minWidth: 260, maxWidth: 360 }}>         
            <LocationSelector
                selectedLocations={selectedLocations}
                onLocationSelect={handleLocationSelect}
                multiSelect={true}
                searchable={true}
                required={false}
                placeholder={t('addUser.fields.locations')}
                disabled={false}
                expandSelectedNodes={isEditMode}
              />
          </Box>          
          <Box sx={{ flex: 1 }}>
<Box sx={{ mb: 2 }}>
<TextField
  label={t('addUser.fields.email') + ' *'}
  value={newUser.email}
  onChange={(e) => handleUserInputChange('email', e.target.value)}
  fullWidth
  size="small"
  placeholder={t('addUser.fields.email')}
  error={!!errors.email}
  helperText={
    errors.email
      ? !newUser.email.trim()
        ? t('addUser.validation.emailRequired')
        : t('addUser.validation.emailInvalid')
      : ""
  }
  inputProps={{ maxLength: 256 }}
  InputLabelProps={{
    shrink: true,
    sx: { color: 'text.primary' }
  }}
/>
</Box>

<Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
  <Box sx={{ flex: 1 }}>
     <TextField 
      label={t('addUser.fields.firstName')}
      value={newUser.firstName}
       onChange={(e) => handleUserInputChange('firstName', e.target.value)}
      required
      fullWidth
      placeholder={t('addUser.fields.firstName')}
      size="small"
      inputProps={{ maxLength: 128 }}
      InputLabelProps={{
    shrink: true,
    required: true, 
    sx: { color: 'text.primary' }
  }}
    />
  </Box>
  <Box sx={{ flex: 1 }}>
    <TextField
      label={t('addUser.fields.lastName')}
      value={newUser.lastName}
      onChange={(e) => handleUserInputChange('lastName', e.target.value)}
      required
      fullWidth
      placeholder={t('addUser.fields.lastName')}
      size="small"
      inputProps={{ maxLength: 128 }}
      InputLabelProps={{
    shrink: true,
    required: true, // 
    sx: { color: 'text.primary' }
  }}
    />
  </Box>
</Box>

<Box sx={{ mb: 2 }}>
  {/* Username */}
<TextField
  label={t('addUser.fields.username') + ' *'}
  value={newUser.username}
  disabled={isEditMode}
  onChange={(e) => handleUserInputChange('username', e.target.value)}
  fullWidth
  size="small"
  placeholder={t('addUser.fields.username')}
  error={!!errors.username}
  helperText={errors.username ? t('addUser.validation.usernameRequired') : ""}
  inputProps={{ maxLength: 256 }}
  InputLabelProps={{
    shrink: true,
    sx: { color: 'text.primary' }
  }}
/>
</Box>


{isEditMode && !showPasswordFields && (
  <Box sx={{ mb: 2 }}>
    <Button
      variant="outlined"
      color="secondary"
      onClick={() => setShowPasswordFields(true)}
      size="large"
    >
      {t('addUser.fields.ResestPassword')}
    </Button>
  </Box>
)}

{(!isEditMode || showPasswordFields) && (
  <>
    <Box sx={{ mb: 2 }}>
      <TextField
        label={isEditMode && showPasswordFields ? t('addUser.fields.password') : t('addUser.fields.password') + ' *'}
        type="password"
        value={newUser.password}
        onChange={(e) => handleUserInputChange('password', e.target.value)}
        fullWidth
        size="small"
        placeholder={t('addUser.fields.password')}
        error={!!errors.password}
        helperText={
          errors.password
            ? !isEditMode && !newUser.password.trim()
              ? t('addUser.validation.passwordRequired')
              : t('apiResponse.addUser.errors.passwordValidation')
            : ""
        }
        InputLabelProps={{
          shrink: true,
          sx: { color: 'text.primary' }
        }}
      />
    </Box>

    <Box sx={{ mb: 2 }}>
      <TextField
        label={isEditMode && showPasswordFields ? t('addUser.fields.confirmPassword') : t('addUser.fields.confirmPassword') + ' *'}
        type="password"
        value={newUser.confirmPassword}
        onChange={(e) => handleUserInputChange('confirmPassword', e.target.value)}
        fullWidth
        size="small"
        placeholder={t('addUser.fields.confirmPassword')}
        error={!!errors.confirmPassword}
        helperText={
          errors.confirmPassword
            ? !isEditMode && !newUser.confirmPassword.trim()
              ? t('addUser.validation.confirmPasswordRequired')
              : t('addUser.validation.passwordMismatch')
            : ""
        }
        inputProps={{ maxLength: 72 }}
        InputLabelProps={{
          shrink: true,
          sx: { color: 'text.primary' }
        }}
      />
    </Box>
  </>
)}


 <FormControl fullWidth  sx={{ mb: 2 }} error={!!errors.userGroup} variant="outlined">
  <InputLabel
    id="user-group-label"
    shrink
    sx={{
      color: 'text.primary',
      '&.Mui-focused': {
        color: 'text.primary',
      },
      backgroundColor: 'background.paper',
      px: 0.5,
      transform: 'translate(14px, -9px) scale(0.75)',
    }}
  >
    {t('addUser.fields.userGroup')} *
  </InputLabel>
  <Select
  label={t('addUser.fields.userGroup')}
  labelId="user-group-label"
  value={newUser.userGroup}
 onChange={(e) => handleUserInputChange('userGroup', e.target.value)}
  disabled={loadingUserGroups}
  displayEmpty
  sx={{
    '& .MuiOutlinedInput-notchedOutline': {
      textAlign: 'left',
      '& legend': {
        visibility: 'visible',
        width: 'auto',
      },
    },
  }}
  MenuProps={{
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    PaperProps: {
      sx: {
        maxHeight: 300,
        maxWidth: '100vw',
        minWidth: 200,
        width: 'auto',
        pl: 2, // <-- Add this line for left padding
      },
    },
  }}
>
  {loadingUserGroups ? (
    <MenuItem disabled>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <CircularProgress size={20} sx={{ mr: 1 }} />
        {t('addUser.messages.loadingUserGroups')}
      </Box>
    </MenuItem>
  ) : userGroups.length > 0 ? (
    userGroups.map((group) => (
      <MenuItem key={group.id} value={group.id.toString()}>
        {group.name}
      </MenuItem>
    ))
  ) : (
    <MenuItem disabled>{t('addUser.messages.loadingUserGroups')}</MenuItem>
  )}
</Select>
  {errors.userGroup && (
    <Typography variant="caption" color="error" sx={{ pl: 2.0 }}>
      {t('addUser.validation.userGroupRequired')}
    </Typography>
  )}
</FormControl>

   <Box sx={{ mb: 2 }}>
    <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>{t('addUser.fields.notes')}</Typography>
    <TextField
      value={newUser.notes}
      onChange={(e) => handleUserInputChange('notes', e.target.value)}
      fullWidth
      multiline
      minRows={3}
      placeholder={t('addUser.fields.notes')}
      InputLabelProps={{ shrink: false }}
    />
    
  </Box>

  {isEditMode && (
    <Box sx={{ mb: 2 }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={newUser.deactivated}
            onChange={(e) => handleUserInputChange('deactivated', e.target.checked.toString())}
            color="primary"
            size="medium"
          />
        }
        label={t('addUser.fields.deactivated')}
      />
    </Box>
  )}
  <Box sx={{ mb: 2 }}>
  {apiError && (
    <Typography
      ref={errorRef}
      tabIndex={-1}
      color="error"
      sx={{ mt: 1, fontSize: '0.875rem' }}
      role="alert"
    >
      {t(apiError, apiError)}
    </Typography>
  )}
   {successMessage && (
    <Typography
      ref={successRef}
      tabIndex={-1}
      color="success.main"
      sx={{ mt: 1, fontSize: '0.875rem' }}
      role="status"
    >
      {successMessage}
    </Typography>
  )}
</Box>
   
          </Box>
        
        </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size='medium' color="primary" variant='text'>
          {t('addUser.actions.cancel')}
        </Button>
        <Button
  onClick={handleAdd}
  size="medium"
  color="primary"
  variant="contained"
  disabled={isSubmitting || isLoadingUserData || Object.values(errors).some(error => error)}
  startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
>
  {isSubmitting ? (isEditMode ? t('addUser.loading.saving') : t('addUser.loading.creating')) : (isEditMode ? t('addUser.actions.update') : t('addUser.actions.save'))}
</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUser;